import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { getToken } from '../../utils/tokenUtils';
import { AuthContext } from '../../contexts/AuthContext';
import { getRoleBasedRoute } from '../../utils/routeUtils';
import {
  Search,
  Filter,
  Eye,
  Edit,
  CheckCircle,
  X,
  Mail,
  Trash2,
  Plus,
  Download,
  HelpCircle,
  Bell,
  ChevronUp,
  ChevronDown,
  MoreVertical,
} from 'lucide-react';
import DropdownMenu from '../../components/UI/DropdownMenu';
import StatusBadge from '../../components/UI/StatusBadge';
import Notification from '../../components/UI/Notification';
import ReactPaginate from 'react-paginate';

const PayrollList = () => {
  const navigate = useNavigate();
  const { roles } = useContext(AuthContext);
  const token = getToken();
  
  // Helper to get role-based route
  const getRoleBasedRouteLocal = (route) => {
    return getRoleBasedRoute(route, roles);
  };
  
  // State management
  const [records, setRecords] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [openMenu, setOpenMenu] = useState(null);
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [appliedFilters, setAppliedFilters] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    status: '',
    department: '',
    searchEmployee: '',
  });
  const [tempFilters, setTempFilters] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    status: '',
    department: '',
    searchEmployee: '',
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 20,
  });
  const [currentPage, setCurrentPage] = useState(1);

  // Months array
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  // Fetch departments
  useEffect(() => {
    fetchDepartments();
  }, []);

  // Fetch records when filters or page changes
  useEffect(() => {
    fetchRecords();
  }, [appliedFilters, currentPage]);

  const fetchDepartments = async () => {
    try {
      const response = await fetch('https://hrmspayroll.prepstripe.com/api/masters/departments', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      setDepartments(data?.data || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchRecords = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        year: appliedFilters.year,
        month: appliedFilters.month,
        page: page,
        per_page: pagination.perPage,
      });

      if (appliedFilters.status) params.append('status', appliedFilters.status);
      if (appliedFilters.department) params.append('department', appliedFilters.department);
      if (appliedFilters.searchEmployee) params.append('search', appliedFilters.searchEmployee);

      const response = await fetch(`https://hrmspayroll.prepstripe.com/api/payroll/monthly-payrolls?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();

      let filteredData = data?.data?.data || [];
      
      // Client-side filtering for employee search if needed
      if (appliedFilters.searchEmployee) {
        const searchLower = appliedFilters.searchEmployee.toLowerCase();
        filteredData = filteredData.filter(record =>
          (record.employee_name || '').toLowerCase().includes(searchLower) ||
          (record.employee_id || '').toLowerCase().includes(searchLower)
        );
      }

      setRecords(filteredData);
      setPagination({
        currentPage: data?.data?.current_page || 1,
        totalPages: data?.data?.last_page || 1,
        totalItems: data?.data?.total || 0,
        perPage: data?.data?.per_page || 20,
      });
    } catch (error) {
      console.error('Error fetching payroll records:', error);
      setNotification({
        type: 'error',
        message: 'Failed to fetch payroll records',
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (selectedPage) => {
    const newPage = selectedPage.selected + 1;
    setCurrentPage(newPage);
    fetchRecords(newPage);
  };

  const handleSelectRecord = (recordId) => {
    setSelectedRecords(prev =>
      prev.includes(recordId)
        ? prev.filter(id => id !== recordId)
        : [...prev, recordId]
    );
  };

  const handleSelectAll = () => {
    if (selectedRecords.length === records.length) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(records.map(record => record.id));
    }
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...tempFilters });
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    const defaultFilters = {
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      status: '',
      department: '',
      searchEmployee: '',
    };
    setTempFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    setCurrentPage(1);
  };

  const handleBatchApprove = async () => {
    if (selectedRecords.length === 0) return;
    
    try {
      setLoading(true);
      const promises = selectedRecords.map(id =>
        fetch(`https://hrmspayroll.prepstripe.com/api/payroll/monthly-payrolls/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ status: 'approved' })
        })
      );
      
      await Promise.all(promises);
      setNotification({
        type: 'success',
        message: `${selectedRecords.length} payroll(s) approved successfully`,
      });
      setSelectedRecords([]);
      fetchRecords(currentPage);
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Failed to approve payrolls',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBatchSendPayslips = async () => {
    if (selectedRecords.length === 0) return;
    
    try {
      setLoading(true);
      // TODO: Implement batch send payslips API call
      setNotification({
        type: 'success',
        message: `Payslips sent to ${selectedRecords.length} employee(s)`,
      });
      setSelectedRecords([]);
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Failed to send payslips',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBatchDelete = async () => {
    if (selectedRecords.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedRecords.length} payroll record(s)?`)) {
      return;
    }

    try {
      setLoading(true);
      const promises = selectedRecords.map(id =>
        fetch(`https://hrmspayroll.prepstripe.com/api/payroll/monthly-payrolls/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        })
      );
      
      await Promise.all(promises);
      setNotification({
        type: 'success',
        message: `${selectedRecords.length} payroll(s) deleted successfully`,
      });
      setSelectedRecords([]);
      fetchRecords(currentPage);
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Failed to delete payrolls',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (recordId) => {
    // Use role-based route to ensure proper navigation
    const route = getRoleBasedRouteLocal(`/admin/payroll/processing/details/${recordId}`);
    navigate(route);
  };

  const handleEdit = (recordId) => {
    navigate(`/payroll/monthly-payrolls/${recordId}/edit`);
  };

  const handleApprove = async (recordId) => {
    try {
      const response = await fetch(`https://hrmspayroll.prepstripe.com/api/payroll/monthly-payrolls/${recordId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ status: 'approved' })
      });
      
      if (response.ok) {
        setNotification({
          type: 'success',
          message: 'Payroll approved successfully',
        });
        fetchRecords(currentPage);
      }
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Failed to approve payroll',
      });
    }
  };

  const handleReject = async (recordId) => {
    try {
      const response = await fetch(`https://hrmspayroll.prepstripe.com/api/payroll/monthly-payrolls/${recordId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ status: 'rejected' })
      });
      
      if (response.ok) {
        setNotification({
          type: 'success',
          message: 'Payroll rejected successfully',
        });
        fetchRecords(currentPage);
      }
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Failed to reject payroll',
      });
    }
  };

  const handleSendPayslip = async (recordId) => {
    try {
      // TODO: Implement send payslip API call
      setNotification({
        type: 'success',
        message: 'Payslip sent successfully',
      });
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Failed to send payslip',
      });
    }
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm('Are you sure you want to delete this payroll record?')) {
      return;
    }

    try {
      const response = await fetch(`https://hrmspayroll.prepstripe.com/api/payroll/monthly-payrolls/${recordId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      
      if (response.ok) {
        setNotification({
          type: 'success',
          message: 'Payroll deleted successfully',
        });
        fetchRecords(currentPage);
      }
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Failed to delete payroll',
      });
    }
  };

  // Actions menu component
  const ActionsColumn = React.memo(({ row }) => {
    const buttonRef = useRef(null);
    return (
      <div className="flex items-center justify-center relative">
        <button
          ref={buttonRef}
          onClick={() => setOpenMenu(openMenu === row.id ? null : row.id)}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          title="Actions"
        >
          <MoreVertical className="h-4 w-4 text-gray-600" />
        </button>
        {openMenu === row.id && (
          <DropdownMenu buttonRef={buttonRef} onClose={() => setOpenMenu(null)}>
            <button
              onClick={() => { handleViewDetails(row.id); setOpenMenu(null); }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <Eye className="h-4 w-4" />
              View Details
            </button>
            <button
              onClick={() => { handleEdit(row.id); setOpenMenu(null); }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <Edit className="h-4 w-4" />
              Edit
            </button>
            {row.status !== 'approved' && (
              <button
                onClick={() => { handleApprove(row.id); setOpenMenu(null); }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                Approve
              </button>
            )}
            {row.status !== 'rejected' && (
              <button
                onClick={() => { handleReject(row.id); setOpenMenu(null); }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                Reject
              </button>
            )}
            <button
              onClick={() => { handleSendPayslip(row.id); setOpenMenu(null); }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <Mail className="h-4 w-4" />
              Send Payslip
            </button>
            <button
              onClick={() => { handleDelete(row.id); setOpenMenu(null); }}
              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          </DropdownMenu>
        )}
      </div>
    );
  });

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Header Section */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Payroll Management
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage and process employee payrolls
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <input
                type="text"
                placeholder="Search employees, payrolls..."
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white w-64"
              />
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
              />
            </div>
            <button
              onClick={() => navigate('/hr/payroll/processing/generate')}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
            >
              <Plus className="h-4 w-4" />
              Generate New Payroll
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              <Download className="h-4 w-4" />
              Export
            </button>
            <button className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              <HelpCircle className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </button>
            <button className="relative p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              <Bell className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="mb-4">
        <button
          onClick={() => setFiltersExpanded(!filtersExpanded)}
          className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <Filter className="h-4 w-4" />
          Filters
          {filtersExpanded ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>
        {filtersExpanded && (
          <div className="mt-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Year
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                  value={tempFilters.year}
                  onChange={(e) => setTempFilters({ ...tempFilters, year: parseInt(e.target.value) })}
                >
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Month
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                  value={tempFilters.month}
                  onChange={(e) => setTempFilters({ ...tempFilters, month: parseInt(e.target.value) })}
                >
                  {months.map((month, index) => (
                    <option key={index} value={index + 1}>{month}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Status
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                  value={tempFilters.status}
                  onChange={(e) => setTempFilters({ ...tempFilters, status: e.target.value })}
                >
                  <option value="">All Statuses</option>
                  <option value="draft">Draft</option>
                  <option value="processed">Processed</option>
                  <option value="approved">Approved</option>
                  <option value="paid">Paid</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Department
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                  value={tempFilters.department}
                  onChange={(e) => setTempFilters({ ...tempFilters, department: e.target.value })}
                >
                  <option value="">All Departments</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.name || dept.department_name}>
                      {dept.name || dept.department_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Search Employee
                </label>
                <input
                  type="text"
                  placeholder="Search by name..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                  value={tempFilters.searchEmployee}
                  onChange={(e) => setTempFilters({ ...tempFilters, searchEmployee: e.target.value })}
                />
              </div>
              <div className="flex items-end gap-2">
                <button
                  onClick={handleResetFilters}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <Filter className="h-4 w-4 rotate-180" />
                  Reset
                </button>
                <button
                  onClick={handleApplyFilters}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Records Count */}
      <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
        Showing {records.length} of {pagination.totalItems} records
      </div>

      {/* Batch Actions Bar */}
      {selectedRecords.length > 0 && (
        <div className="mb-4 flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {selectedRecords.length} item(s) selected
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleBatchApprove}
              className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <CheckCircle className="h-4 w-4" />
              Approve Selected
            </button>
            <button
              onClick={handleBatchSendPayslips}
              className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <Mail className="h-4 w-4" />
              Send Payslips
            </button>
            <button
              onClick={handleBatchDelete}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          </div>
        </div>
      )}

      {/* Table Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-4 text-left">
                  <input
                    type="checkbox"
                    checked={selectedRecords.length === records.length && records.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Employee ID
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Employee Name
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Department
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Period
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Basic Salary
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Gross Salary
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Net Salary
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="10" className="px-6 py-8 text-center text-gray-500">
                    Loading payroll records...
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-6 py-8 text-center text-gray-500">
                    No payroll records found
                  </td>
                </tr>
              ) : (
                records.map((record) => (
                  <tr
                    key={record.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedRecords.includes(record.id)}
                        onChange={() => handleSelectRecord(record.id)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {record.employee_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {record.employee_name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {record.department || record.department_name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {months[record.payroll_month - 1]} {record.payroll_year}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      ₹{record.basic_salary?.toLocaleString() || '0'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      ₹{record.gross_salary?.toLocaleString() || '0'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      ₹{record.net_salary?.toLocaleString() || '0'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={record.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <ActionsColumn row={record} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700 dark:text-gray-300">Rows per page:</span>
          <select
            className="px-3 py-1 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-800 dark:text-white"
            value={pagination.perPage}
            onChange={(e) => {
              setPagination({ ...pagination, perPage: parseInt(e.target.value) });
              setCurrentPage(1);
            }}
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
        <ReactPaginate
          breakLabel="..."
          nextLabel="Next >"
          onPageChange={handlePageChange}
          forcePage={currentPage - 1}
          pageRangeDisplayed={5}
          disabledClassName="opacity-50 cursor-not-allowed"
          disabledLinkClassName="pointer-events-none"
          pageCount={pagination.totalPages}
          previousLabel="< Prev"
          previousClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
          previousLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
          nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
          nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
          renderOnZeroPageCount={null}
          containerClassName="flex items-center gap-2 font-poppins"
          pageClassName="border rounded dark:text-white font-poppins"
          pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
          activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
          activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
        />
        <div className="text-sm text-gray-700 dark:text-gray-300">
          Page {pagination.currentPage} of {pagination.totalPages}
        </div>
      </div>
    </div>
  );
};

export default PayrollList;

